import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { forgotPassword, clearError, selectAuthLoading, selectAuthError } from '../../store/slices/authSlice';
import AuthLayout from './AuthLayout';
import {
  Mail,
  LoaderCircle,
  CheckCircle,
  XCircle,
  ArrowLeft,
  Send
} from 'lucide-react';

const ForgotPassword = ({ showToast }) => {
  const [email, setEmail] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const dispatch = useDispatch();
  const loading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);

  // Styles matching Register component
  const inputStyles = {
    width: '100%',
    padding: '16px 20px 16px 50px',
    fontSize: '17px',
    borderRadius: '10px',
    border: '2px solid #e2e8f0',
    background: '#ffffff',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    color: '#1a1a2e',
    outline: 'none',
    lineHeight: '1.5',
  };

  const labelStyles = {
    display: 'block',
    fontWeight: '600',
    marginBottom: '8px',
    color: '#1a1a2e',
    fontSize: '15px',
  };

  const inputWrapperStyles = {
    marginBottom: '20px',
    position: 'relative',
  };

  const iconWrapperStyles = {
    position: 'absolute',
    left: '18px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#94a3b8',
    pointerEvents: 'none',
    zIndex: 1,
  };

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await dispatch(forgotPassword(email)).unwrap();
      setIsSuccess(true);
      setEmail('');
      if (showToast) {
        showToast('✅ Password reset link sent! Check your email.', 'success');
      }
    } catch (err) {
      if (showToast) {
        showToast(err.message || 'Failed to send reset link', 'error');
      }
    }
  };

  return (
    <AuthLayout 
      title="Forgot Password" 
      subtitle="We'll send you a reset link"
    >
      <form onSubmit={handleSubmit} style={{ maxWidth: '520px', margin: '0 auto' }}>
        {/* Display Error Message */}
        {error && (
          <div style={{ 
            marginBottom: '24px', 
            padding: '16px 20px', 
            borderRadius: '10px',
            fontSize: '15px',
            backgroundColor: '#fee2e2',
            color: '#dc2626',
            border: '1px solid #fecaca',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <XCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {/* Display Success Message */}
        {isSuccess && (
          <div style={{ 
            marginBottom: '24px', 
            padding: '16px 20px', 
            borderRadius: '10px',
            fontSize: '15px',
            backgroundColor: '#d1fae5',
            color: '#065f46',
            border: '1px solid #a7f3d0',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <CheckCircle size={20} />
            <span>Password reset link sent! Please check your email.</span>
          </div>
        )}

        {/* Email Field */}
        <div style={inputWrapperStyles}>
          <label style={labelStyles}>Email Address</label>
          <div style={{ position: 'relative' }}>
            <span style={iconWrapperStyles}><Mail size={20} /></span>
            <input
              type="email"
              style={{
                ...inputStyles,
                borderColor: '#e2e8f0',
                opacity: isSuccess ? 0.6 : 1,
                cursor: isSuccess ? 'not-allowed' : 'text'
              }}
              placeholder="Enter your registered email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isSuccess || loading}
              onFocus={(e) => e.target.style.borderColor = '#d4af37'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>
          <div style={{
            fontSize: '13px',
            color: '#94a3b8',
            marginTop: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <Mail size={14} />
            We'll send a password reset link to this email.
          </div>
        </div>

        {/* Submit Button */}
        <button 
          type="submit" 
          disabled={loading || !email || isSuccess}
          style={{
            width: '100%',
            padding: '18px',
            fontSize: '18px',
            fontWeight: '700',
            borderRadius: '10px',
            backgroundColor: '#d4af37',
            color: '#1a1a2e',
            border: 'none',
            cursor: (loading || !email || isSuccess) ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            opacity: (loading || !email || isSuccess) ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px'
          }}
          onMouseEnter={(e) => {
            if (!loading && email && !isSuccess) e.target.style.backgroundColor = '#c5a032';
          }}
          onMouseLeave={(e) => {
            if (!loading && email && !isSuccess) e.target.style.backgroundColor = '#d4af37';
          }}
        >
          {loading ? (
            <>
              <LoaderCircle size={22} style={{ animation: 'spin 1s linear infinite' }} />
              Sending...
            </>
          ) : (
            <>
              <Send size={20} /> Send Reset Link
            </>
          )}
        </button>

        {/* Back to Login Link */}
        <div style={{ 
          textAlign: 'center', 
          marginTop: '24px'
        }}>
          <Link to="/login" style={{ 
            color: '#64748b',
            textDecoration: 'none',
            fontSize: '15px',
            fontWeight: '500',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'color 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.color = '#d4af37'}
          onMouseLeave={(e) => e.target.style.color = '#64748b'}
          >
            <ArrowLeft size={18} /> Back to Sign In
          </Link>
        </div>
      </form>

      {/* Add spin animation */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </AuthLayout>
  );
};

export default ForgotPassword;